// src/admin/TimerBannerManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { uploadToImageKit } from '../utils/imagekitUpload';
import {
  Plus, Edit, Trash2, X, Upload, Image as ImageIcon,
  Save, Clock, Zap, Calendar, AlertCircle
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const TimerBannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const defaultFormData = {
    title: '',
    description: '',
    image: '',
    buttonText: 'Shop Now',
    buttonLink: '/shop',
    startDate: '',
    startTime: '00:00',
    endDate: '',
    endTime: '23:59',
    active: true,
    discountLabel: '',
  };

  const [formData, setFormData] = useState(defaultFormData);

  // Fetch
  const fetchBanners = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'timerBanners'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setBanners(data);
    } catch (error) {
      console.error('Error fetching timer banners:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMessage({ type: '', text: '' });
    try {
      const url = await uploadToImageKit(file, 'timer-banners');
      setFormData({ ...formData, image: url });
      setMessage({ type: 'success', text: 'Image uploaded!' });
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: 'Image upload failed. Try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Only Title + Image required (baaki optional)
    if (!formData.title || !formData.image) {
      setMessage({ type: 'error', text: 'Title aur Image zaroori hai.' });
      return;
    }

    setSaving(true);

    // Build dates safely
    let startDateTime = null;
    let endDateTime = null;

    try {
      if (formData.startDate) {
        const st = formData.startTime || '00:00';
        startDateTime = new Date(`${formData.startDate}T${st}:00`);
        if (isNaN(startDateTime.getTime())) startDateTime = null;
      }
      if (formData.endDate) {
        const et = formData.endTime || '23:59';
        endDateTime = new Date(`${formData.endDate}T${et}:00`);
        if (isNaN(endDateTime.getTime())) endDateTime = null;
      }
    } catch (err) {
      console.error('Date parse error:', err);
    }

    const bannerData = {
      title: formData.title,
      description: formData.description || '',
      image: formData.image,
      buttonText: formData.buttonText || 'Shop Now',
      buttonLink: formData.buttonLink || '/shop',
      startDate: startDateTime,
      endDate: endDateTime,
      active: formData.active === true,
      discountLabel: formData.discountLabel || '',
    };

    try {
      if (editingBanner) {
        await updateDoc(doc(db, 'timerBanners', editingBanner.id), {
          ...bannerData,
          updatedAt: new Date(),
        });
        setSuccessMessage('Timer Banner updated!');
      } else {
        const newId = Date.now().toString();
        await setDoc(doc(db, 'timerBanners', newId), {
          ...bannerData,
          createdAt: new Date(),
        });
        setSuccessMessage('Timer Banner created!');
      }

      setShowModal(false);
      setShowSuccess(true);
      fetchBanners();

    } catch (error) {
      console.error('Save error:', error);
      let errText = 'Failed to save banner.';
      if (error.code === 'permission-denied') {
        errText = 'Permission denied. Firestore rules check karein.';
      } else if (error.message) {
        errText = 'Error: ' + error.message;
      }
      setMessage({ type: 'error', text: errText });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (banner) => {
    if (!window.confirm(`Delete timer banner "${banner.title}"?`)) return;
    try {
      await deleteDoc(doc(db, 'timerBanners', banner.id));
      setSuccessMessage('Timer banner deleted!');
      setShowSuccess(true);
      fetchBanners();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete.');
    }
  };

  const openAddModal = () => {
    setEditingBanner(null);
    // Default: start today, end after 7 days
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);

    const fmtDate = (d) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    setFormData({
      ...defaultFormData,
      startDate: fmtDate(today),
      endDate: fmtDate(nextWeek),
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);

    const toDateInput = (ts) => {
      if (!ts) return '';
      try {
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      } catch {
        return '';
      }
    };

    const toTimeInput = (ts) => {
      if (!ts) return '00:00';
      try {
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      } catch {
        return '00:00';
      }
    };

    setFormData({
      title: banner.title || '',
      description: banner.description || '',
      image: banner.image || '',
      buttonText: banner.buttonText || 'Shop Now',
      buttonLink: banner.buttonLink || '/shop',
      startDate: toDateInput(banner.startDate),
      startTime: toTimeInput(banner.startDate),
      endDate: toDateInput(banner.endDate),
      endTime: toTimeInput(banner.endDate),
      active: banner.active !== false,
      discountLabel: banner.discountLabel || '',
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const isLiveNow = (banner) => {
    if (banner.active === false) return false;
    const now = new Date();
    try {
      if (banner.startDate) {
        const start = banner.startDate.toDate ? banner.startDate.toDate() : new Date(banner.startDate);
        if (now < start) return false;
      }
      if (banner.endDate) {
        const end = banner.endDate.toDate ? banner.endDate.toDate() : new Date(banner.endDate);
        if (now > end) return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  const formatDateTime = (ts) => {
    if (!ts) return '-';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleString('en-PK', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-6">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Zap className="text-green-700" size={26} /> Flash Sale / Timer Banners
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Home page par timer ke saath flash sale banner show karein.
          </p>
        </div>
        <button onClick={openAddModal}
          className="bg-green-700 text-white px-5 py-3 rounded-lg hover:bg-green-800 transition flex items-center gap-2 font-semibold shadow-md">
          <Plus size={18} /> Add Timer Banner
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      ) : banners.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Zap size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No timer banners yet. Add your first one!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {banners.map(banner => {
            const live = isLiveNow(banner);
            return (
              <div key={banner.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
                <div className="relative h-40 bg-gray-100">
                  <img src={banner.image} alt={banner.title} className="w-full h-full object-cover" />
                  <span className={`absolute top-2 left-2 text-xs font-bold px-2 py-1 rounded-full ${
                    live ? 'bg-green-500 text-white anim-pulseGlow'
                    : (banner.active === false ? 'bg-gray-500 text-white' : 'bg-yellow-500 text-white')
                  }`}>
                    {live ? '● LIVE NOW' : (banner.active === false ? 'INACTIVE' : 'SCHEDULED')}
                  </span>
                  {banner.discountLabel && (
                    <span className="absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded-full bg-red-500 text-white">
                      {banner.discountLabel}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 line-clamp-1">{banner.title}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-1">{banner.description || 'No description'}</p>

                  <div className="mt-3 space-y-1 text-xs text-gray-600">
                    <p className="flex items-center gap-1">
                      <Calendar size={12} className="text-green-600" />
                      <span className="font-semibold">Start:</span> {formatDateTime(banner.startDate)}
                    </p>
                    <p className="flex items-center gap-1">
                      <Clock size={12} className="text-red-500" />
                      <span className="font-semibold">End:</span> {formatDateTime(banner.endDate)}
                    </p>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button onClick={() => openEditModal(banner)}
                      className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1">
                      <Edit size={14} /> Edit
                    </button>
                    <button onClick={() => handleDelete(banner)}
                      className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1">
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">

            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-800">
                {editingBanner ? 'Edit Timer Banner' : 'Add Timer Banner'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">

              {message.text && (
                <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {message.type === 'error' && <AlertCircle size={16} />}
                  {message.text}
                </div>
              )}

              {/* Image */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Banner Image *</label>
                {formData.image ? (
                  <div className="relative">
                    <img src={formData.image} alt="preview" className="w-full h-48 object-cover rounded-lg" />
                    <label className="absolute bottom-2 right-2 bg-white text-gray-700 px-3 py-1 rounded-lg shadow-md text-xs font-semibold cursor-pointer hover:bg-gray-50">
                      Change
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    </label>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition">
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
                        <p className="mt-3 text-sm text-gray-600">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="text-gray-400 mb-2" size={32} />
                        <p className="text-sm font-semibold text-gray-600">Click to upload image</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG (Max 5 MB)</p>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </label>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
                <input type="text" value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., Mega Flash Sale" />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description (Optional)</label>
                <textarea value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none"
                  placeholder="Short description..." />
              </div>

              {/* Discount Label */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Discount Label (Optional)</label>
                <input type="text" value={formData.discountLabel}
                  onChange={(e) => setFormData({ ...formData, discountLabel: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., 50% OFF" />
              </div>

              {/* Button Text + Link */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Button Text (Optional)</label>
                  <input type="text" value={formData.buttonText}
                    onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="Shop Now" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Button Link (Optional)</label>
                  <input type="text" value={formData.buttonLink}
                    onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="/shop" />
                  <p className="text-xs text-gray-500 mt-1">
                    Product ke liye: <code>/product/PRODUCT_ID</code>
                  </p>
                </div>
              </div>

              {/* Start Date + Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date (Optional)</label>
                  <input type="date" value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Start Time</label>
                  <input type="time" value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>
              </div>

              {/* End Date + Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Date (Optional)</label>
                  <input type="date" value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Time</label>
                  <input type="time" value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>
              </div>

              <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-800">
                ℹ️ Start/End Date khaali chhodein to banner <strong>hamesha show hoga</strong> (jab tak Active hai).
              </div>

              {/* Active */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 text-green-700" />
                <span className="text-sm font-semibold text-gray-700">Enable this banner (Show on website)</span>
              </label>

              <div className="flex gap-3 pt-2 border-t border-gray-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit" disabled={uploading || saving}
                  className={`flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition flex items-center justify-center gap-2 ${(uploading || saving) ? 'opacity-70 cursor-not-allowed' : ''}`}>
                  <Save size={18} /> {saving ? 'Saving...' : (editingBanner ? 'Update' : 'Create')}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message={successMessage}
        buttonText="Great!"
      />

    </div>
  );
};

export default TimerBannerManagement;